import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {  SectionHeader } from '../../../common';
import { State } from '../reducer';
import { Button } from '../../../../components/Buttons';
import styled from 'styled-components/macro';
import { Checkbox } from '../../../../components/Forms';
import { Loading, LoadingOverlay } from '../../../../components/Loading';
import { colors } from '../../../../styles/constants';
import Icon from '../../../../components/Icons';
import AttributeInput from './AttributeInput';
import ErrorModal from './ErrorModal';

interface Props {
    attributes: any | undefined;
    accountId: string;
    customerId: string;
    attributesState: State;
    editCustomerAttributes: (accountId: string, id: string, attributes: any) => void;
    fetchCustomersSettings: (accountId: string) => void;
    clearError: () => void;
}

const Attributes = (
    {
        attributes,
        accountId,
        customerId,
        attributesState,
        editCustomerAttributes,
        fetchCustomersSettings,
        clearError,
    }: Props
) => {
    const { t }=useTranslation();
    const { settingsByAccountId } = attributesState;
    const customersSettings = settingsByAccountId[accountId];

    const saveEditedAttributes = (values: any) => {
        editCustomerAttributes(accountId, customerId, values);
    };

    useEffect(() => {
        if (!customersSettings) {
            fetchCustomersSettings(accountId);
        }
    }, [accountId, fetchCustomersSettings, customersSettings]);

    const [addedFields, setFieldsAdded] = useState<string[]>([]);

    return (
        <Container>
            <SectionHeader>{t('customers.customer.attributes.title')}</SectionHeader>
            {!customersSettings && <Center><Loading /></Center>}
            {(customersSettings?.attributes || []).map((attribute) => {
                if (attributes[attribute.name] === null || !attributes.hasOwnProperty(attribute.name)) {
                    return <SpacedWrapper key={attribute.name}>
                        <Button type="button" className="alt" onClick={() => {
                            if (attribute.type === 'boolean') {
                                setFieldsAdded([...addedFields, attribute.name]);
                                saveEditedAttributes({ [attribute.name]: true });
                            } else {
                                setFieldsAdded([...addedFields, attribute.multiple_values ? `${attribute.name}-0`: attribute.name]);
                                saveEditedAttributes({ [attribute.name]: attribute.multiple_values ? [''] :'' });
                            }
                        }}>{t('customers.customer.attributes.add_field', { name: attribute.name })}</Button>
                    </SpacedWrapper>;
                }
                if (attribute) {
                    if (attribute.type === 'boolean') {
                        return (<SpacedWrapper key={attribute.name}>
                            <Checkbox name={`attributes.${attribute.name}`} label={attribute.name} checked={attributes[attribute.name]} onChange={() => {
                                saveEditedAttributes({ [attribute.name]: !attributes[attribute.name] });
                            }}  />
                            <Button type="button" title={t('customers.customer.attributes.remove')} onClick={() => {
                                saveEditedAttributes({ [attribute.name]: null });
                            }} className="alt tiny">
                                <Icon icon="close_small" fill={colors.invalid} />
                            </Button>
                        </SpacedWrapper>
                        );
                    } else {
                        if (attribute.multiple_values) {
                            return <Group>
                                <Label>{attribute.name}</Label>
                                <Inset>
                                    {attributes[attribute.name].map((val: any, i: number) => (
                                        <SpacedWrapper key={`${attribute.name}-${i}-${val}`}>
                                            <AttributeInput
                                                initialIsEditing={addedFields.includes(`${attribute.name}-${i}`)}
                                                placeholder={attribute.type}
                                                name={`${attribute.name}-${i}`}
                                                label={`${i + 1}:`}
                                                inlineLabel={true}
                                                value={val}
                                                onChange={(value) => {
                                                    setFieldsAdded(addedFields.filter((a) => a !== `${attribute.name}-${i}`));
                                                    const newValues = [...attributes[attribute.name]];
                                                    newValues[i] = value;
                                                    saveEditedAttributes({ [attribute.name]: newValues });
                                                }}
                                                onRemove={() => {
                                                // remove element from array, we cannot set the value to null because
                                                // there is a bug in the customer service that does not allow null values
                                                // for arrays
                                                    const next = [...attributes[attribute.name]];
                                                    next.splice(i, 1);
                                                    setFieldsAdded(addedFields.filter((a) => a !== `${attribute.name}-${i}`));
                                                    saveEditedAttributes({ [attribute.name]: next });
                                                }}
                                            />
                                        </SpacedWrapper>
                                    ))}
                                    <SpacedWrapper>
                                        <Button type="button" className="alt"  onClick={() => {
                                            setFieldsAdded([...addedFields, `${attribute.name}-${attributes[attribute.name].length}`]);
                                            saveEditedAttributes({ [attribute.name]: [...attributes[attribute.name], '']});
                                        }}>{t('customers.customer.attributes.add_new_item_to_list', { name: attribute.name })}</Button>
                                    </SpacedWrapper>
                                </Inset>

                            </Group>;
                        }
                        return (
                            <SpacedWrapper key={attribute.name}>
                                <AttributeInput
                                    initialIsEditing={addedFields.includes(attribute.name)}
                                    placeholder={attribute.type}
                                    name={attribute.name}
                                    label={attribute.name}
                                    inlineLabel={false}
                                    value={attributes[attribute.name]}
                                    onChange={(value) => {
                                        setFieldsAdded(addedFields.filter((a) => a !== attribute.name));
                                        saveEditedAttributes({ [attribute.name]: value });
                                    }}
                                    onRemove={() => {
                                        setFieldsAdded(addedFields.filter((a) => a !== attribute.name));
                                        saveEditedAttributes({ [attribute.name]: null });
                                    }}
                                />
                            </SpacedWrapper>
                        );
                    }
                }
                return null;
            })}
            {attributesState.isLoading && <LoadingOverlay />}
            <ErrorModal error={attributesState.error} onClose={clearError} />
        </Container>
    );
};

export default Attributes;



const Label  = styled.div`
    margin-top: 16px;
    font-weight: bold;
`;

const Group  = styled.div`
    border-bottom: 1px solid ${colors.backgroundAlt};
`;

const Inset  = styled.div`
    padding-left: 36px;
`

;const Container = styled.div`
    flex: 0 0 50%;
    position: relative;
    @media (max-width: 1024px) {
        flex: 1 0 100%;
    }
`;

const SpacedWrapper = styled.div`
    margin-top: 16px;
    margin-bottom: 16px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 16px;
`;

const Center = styled.div`
    margin: 64px;
    display: flex;
    justify-content: center;
    align-items: center;
`;
